// PecTutor - Interactive Tutorial System
// Main export file

import PecTutorComponent from './PecTutor.vue';
import { createPecTutorAPI } from './composables/useTutorialManager';
import type { PecTutorAPI, PecTutorConfig } from './types';

// Default configuration
const defaultConfig = {
  theme: {
    primaryColor: "#007bff",
    secondaryColor: "#6c757d",
    backgroundColor: "#ffffff",
    textColor: "#333333",
    borderRadius: "8px"
  },
  timeouts: {
    defaultMessage: 5000,
    tipFadeOut: 3000,
    userInputReminder: 10000,
    attentionShake: 1000
  },
  animation: {
    mascotTransition: 300,
    messageAppear: 200,
    shakeIntensity: 10,
    fadeOutDuration: 500
  },
  behavior: {
    autoHide: true,
    rememberProgress: true,
    showMenuOnLoad: false,
    enableSounds: false
  },
  mascot: {
    defaultPosition: "bottom-right",
    size: {
      width: 120,
      height: 120
    }
  }
};

let pecTutorInstance: PecTutorAPI | null = null;

// Main PecTutor API factory
export function createPecTutor(config?: Partial<PecTutorConfig>): PecTutorAPI {
  if (pecTutorInstance) {
    console.warn('PecTutor instance already exists. Returning existing instance.');
    return pecTutorInstance;
  }

  const mergedConfig = { ...defaultConfig, ...config } as PecTutorConfig;
  pecTutorInstance = createPecTutorAPI(mergedConfig);
  
  return pecTutorInstance;
}

// Global PecTutor instance (singleton pattern)
export const PecTutor = createPecTutor();

// Immediately register on window for global access
if (typeof window !== 'undefined') {
  (window as Window & { PecTutor?: PecTutorAPI }).PecTutor = PecTutor;
}

// Export the Vue component for manual integration
export { PecTutorComponent };

// Export types for TypeScript users
export type {
  PecTutorAPI,
  PecTutorConfig,
  MessageOptions,
  QuestionOptions,
  ClickOptions,
  DragOptions,
  WaitOptions,
  MediaOptions,
  Tutorial,
  TutorialFunction,
  MascotState
} from './types';

// Auto-install plugin for Vue apps
export default {
  install(app: { component: (name: string, component: unknown) => void; provide: (key: string, value: unknown) => void; config: { globalProperties: Record<string, unknown> } }, options?: Partial<PecTutorConfig>) {
    // Create or get PecTutor instance
    const tutorInstance = pecTutorInstance || createPecTutor(options);
    
    // Register global component
    app.component('PecTutor', PecTutorComponent);
    
    // Provide PecTutor instance globally
    app.provide('pecTutor', tutorInstance);
    app.config.globalProperties.$pecTutor = tutorInstance;
    
    // Make it available on window for external scripts
    if (typeof window !== 'undefined') {
      (window as unknown as { PecTutor: PecTutorAPI }).PecTutor = tutorInstance;
    }
  }
};
